import type { AdsFeedImportMode } from "./adsFeedImportSectionTypes";

export function AdsAssistantFeedImportGooglePanel(props: {
  embedded: boolean | undefined;
  mode: AdsFeedImportMode;
  googleLoading: boolean;
  googleConfigured: boolean;
  googleUpdatedAt: string | null;
  googleEncryptionConfigured: boolean;
  canEditGoogleCredentials: boolean;
  saveCredentialsDraft: string;
  setSaveCredentialsDraft: (v: string) => void;
  saveVaultBusy: boolean;
  googleBanner: string | null;
  saveOrgCredentials: () => void;
  clearOrgCredentials: () => void;
}) {
  const {
    embedded,
    mode,
    googleLoading,
    googleConfigured,
    googleUpdatedAt,
    googleEncryptionConfigured,
    canEditGoogleCredentials,
    saveCredentialsDraft,
    setSaveCredentialsDraft,
    saveVaultBusy,
    googleBanner,
    saveOrgCredentials,
    clearOrgCredentials,
  } = props;

  if (mode !== "google_gbp_fetch") return null;

  return (
    <div
      className={
        (embedded ? "mt-0" : "mt-3") +
        " rounded-lg border border-indigo-200/80 bg-white/70 px-3 py-2 dark:border-indigo-900/50 dark:bg-zinc-900/40"
      }
    >
      {googleLoading ? (
        <p className="text-[11px] text-gray-500">Loading Google import settings…</p>
      ) : (
        <>
          <p className="text-[11px] text-gray-700 dark:text-gray-300">
            <strong>Google service account:</strong>{" "}
            {googleConfigured
              ? `saved (${googleUpdatedAt ? new Date(googleUpdatedAt).toLocaleString() : "unknown time"})`
              : "not saved yet"}
            {googleEncryptionConfigured ? "" : " · server encryption key missing"}
          </p>
          <p className="mt-1 text-[10px] text-gray-600 dark:text-gray-400 max-w-prose">
            Advanced direct GBP API. Prefer CRM Social Planner sync when your org posts through CRM. Invite the service
            account as a manager on your Google Business Profile.
          </p>
          {!canEditGoogleCredentials && !googleConfigured && googleEncryptionConfigured ? (
            <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-200">
              Ask marketing, admin, owner, or dev to save Google credentials before running direct GBP fetch.
            </p>
          ) : null}
          {canEditGoogleCredentials ? (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-semibold text-indigo-900 dark:text-indigo-200">
                Save Google service account JSON for everyone
              </summary>
              <textarea
                className="mt-1 w-full min-h-[4rem] rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-[10px] font-mono dark:border-gray-600 dark:bg-zinc-950 dark:text-gray-100"
                value={saveCredentialsDraft}
                onChange={(e) => setSaveCredentialsDraft(e.target.value)}
                placeholder='Paste service account JSON (client_email + private_key)'
                disabled={saveVaultBusy || !googleEncryptionConfigured}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={saveVaultBusy || !googleEncryptionConfigured}
                  onClick={() => void saveOrgCredentials()}
                  className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saveVaultBusy ? "Saving…" : "Save for org"}
                </button>
                <button
                  type="button"
                  disabled={saveVaultBusy || !googleConfigured}
                  onClick={() => void clearOrgCredentials()}
                  className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-900 dark:text-gray-200"
                >
                  Clear saved
                </button>
              </div>
            </details>
          ) : null}
        </>
      )}
      {googleBanner ? <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-300">{googleBanner}</p> : null}
    </div>
  );
}
